import { useCallback, useEffect, useState } from "react";
import { api, errorMessage, post } from "@/lib/api";
import { Dialog } from "@/components/modals";
import { useNotification } from "@/contexts/NotificationContext";
import { ProposalSyllabusDiff } from "./ProposalSyllabusPages";
import { refreshProposalNavCounts } from "./ProposalNavCount";
import "./SchoolProposalList.css";

type ProposalChange = {
  key: string;
  label: string;
  current: string | null;
  proposed: string;
  syllabus?: { currentId: string | null; proposedId: string | null };
};

type SchoolProposal = {
  id: string;
  courseId: string;
  courseTitle: string;
  createdAt: string;
  summary: string;
  changes: ProposalChange[];
};

/**
 * School-wide pending course requests. Sits on the Courses page.
 * Remove this component and its one call site to take the list back out.
 */
export default function SchoolProposalList({ schoolId }: { schoolId: string }) {
  const { toast, notice } = useNotification();
  const [rows, setRows] = useState<SchoolProposal[] | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const envelope = await api<{ success: boolean; data?: SchoolProposal[] }>(
        `/api/courses/schools/${encodeURIComponent(schoolId)}/proposals`,
      );
      setRows(Array.isArray(envelope.data) ? envelope.data : []);
    } catch (err) {
      setRows([]);
      notice({
        type: "error",
        title: "Could not load course requests",
        subtext: errorMessage(err),
        duration: 7000,
      });
    }
  }, [schoolId, notice]);

  useEffect(() => {
    setRows(null);
    void load();
  }, [load]);

  const review = async (row: SchoolProposal, action: "apply" | "reject") => {
    if (action === "reject") {
      const ok = window.confirm(
        "Discard this proposed change?\n\nThe live course is not changed.",
      );
      if (!ok) return;
    }
    setBusyId(row.id);
    try {
      await post(
        `/api/courses/${encodeURIComponent(row.courseId)}/proposals/${encodeURIComponent(row.id)}/${action}`,
        {},
      );
      toast.success(action === "apply" ? "Proposal applied" : "Proposal discarded");
      setOpenId(null);
      void refreshProposalNavCounts();
      await load();
    } catch (err) {
      notice({
        type: "error",
        title: action === "apply" ? "Could not apply proposal" : "Could not reject proposal",
        subtext: errorMessage(err),
        duration: 7000,
      });
    } finally {
      setBusyId(null);
    }
  };

  if (!rows?.length) return null;

  const openRow = rows.find((row) => row.id === openId) ?? null;

  return (
    <section className="al-school-proposals">
      <h2>Pending proposals ({rows.length})</h2>
      <ul>
        {rows.map((row) => (
          <li key={row.id}>
            <button type="button" className="al-school-proposals__summary" onClick={() => setOpenId(row.id)}>
              <span className="al-school-proposals__title">
                {row.courseTitle}
                <span className="al-school-proposals__what"> · {row.summary}</span>
              </span>
              {row.createdAt ? (
                <span className="al-school-proposals__when">{new Date(row.createdAt).toLocaleString()}</span>
              ) : null}
            </button>
            <span className="al-school-proposals__actions">
              <button type="button" className="al-school-proposals__btn al-school-proposals__btn--ghost" disabled={Boolean(busyId)} onClick={() => setOpenId(row.id)}>
                View
              </button>
              <button type="button" className="al-school-proposals__btn al-school-proposals__btn--ghost" disabled={Boolean(busyId)} onClick={() => void review(row, "reject")}>
                {busyId === row.id ? "…" : "Reject"}
              </button>
              <button type="button" className="al-school-proposals__btn" disabled={Boolean(busyId)} onClick={() => void review(row, "apply")}>
                {busyId === row.id ? "…" : "Apply"}
              </button>
            </span>
          </li>
        ))}
      </ul>

      <Dialog
        open={Boolean(openRow)}
        title={openRow ? `${openRow.courseTitle} · ${openRow.summary}` : "Proposal"}
        subtitle={openRow?.createdAt ? new Date(openRow.createdAt).toLocaleString() : undefined}
        width="40rem"
        maxHeight="80vh"
        onClose={() => setOpenId(null)}
        footer={
          openRow ? (
            <>
              <button
                type="button"
                className="al-school-proposals__btn al-school-proposals__btn--ghost"
                disabled={Boolean(busyId)}
                onClick={() => void review(openRow, "reject")}
              >
                {busyId === openRow.id ? "…" : "Reject"}
              </button>
              <button
                type="button"
                className="al-school-proposals__btn"
                disabled={Boolean(busyId)}
                onClick={() => void review(openRow, "apply")}
              >
                {busyId === openRow.id ? "…" : "Apply"}
              </button>
            </>
          ) : null
        }
      >
        {openRow ? (
          <>
            <dl>
              {openRow.changes.map((change) => (
                <div key={change.key} className="al-school-proposals__field">
                  <dt>{change.label}</dt>
                  <dd>
                    {change.syllabus ? (
                      <ProposalSyllabusDiff
                        courseId={openRow.courseId}
                        currentId={change.syllabus.currentId}
                        proposedId={change.syllabus.proposedId}
                      />
                    ) : change.current != null && change.current !== change.proposed ? (
                      <div className="al-school-proposals__diff">
                        <p>
                          <span className="al-school-proposals__label">Now</span>
                          {change.current}
                        </p>
                        <p>
                          <span className="al-school-proposals__label">Proposed</span>
                          {change.proposed}
                        </p>
                      </div>
                    ) : (
                      <p className="al-school-proposals__value">{change.proposed}</p>
                    )}
                  </dd>
                </div>
              ))}
            </dl>
            <p className="al-school-proposals__note">
              Apply writes this onto the live course. Reject leaves the live course as it is.
            </p>
          </>
        ) : null}
      </Dialog>
    </section>
  );
}
